
/**
 * @file authConfig.ts
 * @description This file contains the configuration and initialization for MSAL (Microsoft Authentication Library) 
 * to enable authentication and authorization in the application. It includes the MSAL configuration object, 
 * login request scopes, Microsoft Graph API endpoints, and the initialization of the PublicClientApplication instance.
 */

import { Configuration, PopupRequest, PublicClientApplication } from "@azure/msal-browser";
import {
    AZURE_APP_CLIENT_ID,
    AZURE_APP_AUTHORITY,
    APP_REDIRECT_URI,
    APP_POST_LOGOUT_REDIRECT_URI
} from '../utils/constants';

/**
    * @constant msalConfig
    * @type {Configuration}
    * @description Configuration object for MSAL. It includes authentication settings such as client ID, authority, 
    * redirect URI, and post-logout redirect URI. It also includes system settings like platform broker configuration.
    * 
    * @property {object} auth - Authentication settings for MSAL.
    * @property {string} auth.clientId - The client ID of the Azure AD application.
    * @property {string} auth.authority - The authority URL for Azure AD authentication.
    * @property {string} auth.redirectUri - The URI where the user will be redirected after login.
    * @property {string} auth.postLogoutRedirectUri - The URI where the user will be redirected after logout.
    * 
    * @property {object} system - System settings for MSAL.
    * @property {boolean} system.allowPlatformBroker - Disables the Windows Authentication Manager (WAM) broker.
    */

export const msalConfig: Configuration = {
    auth: {
        clientId: AZURE_APP_CLIENT_ID,
        authority: AZURE_APP_AUTHORITY,
        redirectUri: APP_REDIRECT_URI,
        postLogoutRedirectUri: APP_POST_LOGOUT_REDIRECT_URI,
    },
    system: {
        allowPlatformBroker: false, // Disables WAM Broker
    },
};

/**
   * @constant loginRequest
   * @type {PopupRequest}
   * @description Defines the scopes for the ID token to be used at Microsoft Identity Platform endpoints.
   * 
   * @property {string[]} scopes - An array of scopes required for the application. In this case, it includes "User.Read".
   */

export const loginRequest: PopupRequest = {
    scopes: ["User.Read"],
};

/**
   * @constant msalInstance
   * @type {PublicClientApplication}
   * @description An instance of the PublicClientApplication class initialized with the MSAL configuration. 
   * This instance is used to handle authentication and acquire tokens for the application.
   */

export const msalInstance = new PublicClientApplication(msalConfig);

